import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gavel, Trophy, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useStore } from "../context/StoreContext";
import api, { money, formatApiError } from "../lib/api";

function fmtLeft(ms) {
  if (ms <= 0) return "00:00:00";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const p = (n) => String(n).padStart(2, "0");
  return d > 0 ? `${d}d ${p(h)}:${p(m)}:${p(ss)}` : `${p(h)}:${p(m)}:${p(ss)}`;
}

function parseBRL(v) {
  const clean = String(v || "").replace(/[^\d.,]/g, "");
  if (!clean) return 0;
  const norm = clean.includes(",") ? clean.replace(/\./g, "").replace(",", ".") : clean;
  const n = parseFloat(norm);
  return isNaN(n) ? 0 : Math.round(n * 100);
}

export default function AuctionPanel({ auction: initialAuction }) {
  const { user } = useStore();
  const [auction, setAuction] = useState(initialAuction);
  const [amount, setAmount] = useState("");
  const [sending, setSending] = useState(false);
  const [left, setLeft] = useState(0);

  useEffect(() => setAuction(initialAuction), [initialAuction]);

  const target = auction.status === "scheduled" ? auction.start_at : auction.end_at;
  useEffect(() => {
    const tick = () => setLeft(new Date(target).getTime() - Date.now());
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [target]);

  const refresh = () =>
    api.get(`/auctions/by-product/${auction.product_id}`)
      .then((r) => { if (r.data?.auction) setAuction(r.data.auction); })
      .catch(() => {});

  useEffect(() => {
    const i = setInterval(refresh, 15000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auction.product_id]);

  const bid = async () => {
    const cents = parseBRL(amount);
    if (cents < auction.min_next_bid) {
      toast.error(`Lance mínimo agora: ${money(auction.min_next_bid)}`);
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/auctions/${auction.id}/bid`, { amount: cents });
      toast.success(r.data.extended ? "Lance registrado! Leilão estendido em +5 min (anti-snipe)" : "Lance registrado! Você está na frente.");
      setAmount("");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível registrar o lance"));
    } finally {
      setSending(false);
    }
  };

  const scheduled = auction.status === "scheduled";
  const ended = auction.status === "ended";

  return (
    <div className="border border-gold/30 rounded-lg bg-[#111111] p-6 gold-glow" data-testid="auction-panel">
      <div className="flex items-center gap-2.5 mb-5">
        <Gavel size={18} className="text-gold" />
        <p className="label-caps">{ended ? "Leilão encerrado" : scheduled ? "Leilão agendado" : "Leilão ativo"}</p>
      </div>

      {scheduled && (
        <>
          <p className="text-zinc-400 text-sm">Os lances abrem em:</p>
          <p className="font-display text-3xl text-gold mt-2" data-testid="auction-countdown">{fmtLeft(left)}</p>
          <p className="text-sm text-zinc-400 mt-4">Lance inicial: <span className="text-zinc-200">{money(auction.starting_price)}</span></p>
        </>
      )}

      {!scheduled && !ended && (
        <>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs text-zinc-500">{auction.current_bid ? "Lance atual" : "Lance inicial"}</p>
              <p className="font-display text-3xl text-gold" data-testid="auction-current-bid">{money(auction.current_bid?.amount ?? auction.starting_price)}</p>
              {auction.current_bid && (
                <p className="text-xs text-zinc-500 mt-1" data-testid="auction-leader">por {auction.current_bid.user_name} · {auction.bids_count} lance(s)</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-zinc-500 flex items-center gap-1.5 justify-end"><Clock size={12} /> Termina em</p>
              <p className="font-display text-2xl text-zinc-100" data-testid="auction-countdown">{fmtLeft(left)}</p>
            </div>
          </div>

          {user ? (
            <div className="mt-6 flex gap-2">
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Mínimo ${money(auction.min_next_bid)}`}
                inputMode="decimal" data-testid="bid-input" className="bg-[#141414] border-white/10 focus:border-amber-500" />
              <Button onClick={bid} disabled={sending} data-testid="place-bid-button"
                className="bg-gold text-black hover:bg-gold-light font-semibold shrink-0 px-6 transition-colors disabled:opacity-50">
                {sending ? "Enviando..." : "Dar lance"}
              </Button>
            </div>
          ) : (
            <Button asChild className="mt-6 w-full bg-gold text-black hover:bg-gold-light font-semibold transition-colors" data-testid="bid-login-button">
              <Link to="/login">Entre para dar um lance</Link>
            </Button>
          )}
          <p className="text-[11px] text-zinc-600 mt-3">
            Lance mínimo: {money(auction.min_next_bid)} (incremento de {money(auction.min_increment)}). Lances nos últimos 5 minutos estendem o leilão em +5 min.
          </p>

          {auction.bids?.length > 0 && (
            <div className="mt-6 border-t border-white/10 pt-4">
              <p className="text-xs text-zinc-500 mb-3">Maiores lances</p>
              <div className="space-y-2">
                {auction.bids.map((b) => (
                  <div key={b.id} className="flex justify-between text-sm" data-testid={`bid-row-${b.id}`}>
                    <span className="text-zinc-300">{b.name}</span>
                    <span className="text-gold">{money(b.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {ended && (
        <div data-testid="auction-result">
          {auction.winner ? (
            <div className="flex items-center gap-3">
              <Trophy size={20} className="text-gold shrink-0" />
              <p className="text-sm text-zinc-300">
                Vencedor: <span className="text-gold">{auction.winner.name}</span> com lance de <span className="text-gold">{money(auction.winner.amount)}</span>
              </p>
            </div>
          ) : (
            <p className="text-sm text-zinc-400">Leilão encerrado sem lances.</p>
          )}
        </div>
      )}
    </div>
  );
}
